import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Cliente, CcMovimiento } from '@/types/database'

export interface ClienteFiltros {
  busqueda?: string
  tipo?: 'minorista' | 'mayorista'
  soloActivos?: boolean
}

export function useClientes(filtros: ClienteFiltros = {}) {
  return useQuery({
    queryKey: ['clientes', filtros],
    queryFn: () => {
      const params = new URLSearchParams()
      if (filtros.tipo)              params.set('tipo', filtros.tipo)
      if (filtros.busqueda?.trim())  params.set('search', filtros.busqueda.trim())
      return api.get<Cliente[]>(`/api/clientes?${params}`)
    },
    staleTime: 30_000,
  })
}

export function useCcMovimientos(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: ['cc-movimientos', clienteId],
    queryFn: async () => {
      const data = await api.get<{ movimientos: CcMovimiento[] }>(`/api/clientes/${clienteId}/cuenta-corriente`)
      return data.movimientos
    },
    enabled: !!clienteId,
    staleTime: 20_000,
  })
}

export function useCrearCliente() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      nombre: string; tipo: 'minorista' | 'mayorista'
      telefono: string; email: string; direccion: string; limiteCredito: number
    }) =>
      api.post<Pick<Cliente, 'id' | 'nombre'>>('/api/clientes', {
        nombre:         input.nombre.trim(),
        tipo:           input.tipo,
        telefono:       input.telefono.trim() || null,
        email:          input.email.trim() || null,
        direccion:      input.direccion.trim() || null,
        limite_credito: input.limiteCredito,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clientes'] }),
  })
}

export function useEditarCliente() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      id: string; nombre: string; tipo: 'minorista' | 'mayorista'
      telefono: string; email: string; direccion: string; limiteCredito: number
    }) =>
      api.put(`/api/clientes/${input.id}`, {
        nombre:         input.nombre.trim(),
        tipo:           input.tipo,
        telefono:       input.telefono.trim() || null,
        email:          input.email.trim() || null,
        direccion:      input.direccion.trim() || null,
        limite_credito: input.limiteCredito,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clientes'] }),
  })
}

export function useToggleActivoCliente() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      api.put(`/api/clientes/${id}`, { activo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clientes'] }),
  })
}

export function useRegistrarMovimientoCC() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ cliente, tipo, monto, notas }: {
      cliente: Cliente
      tipo: 'cargo' | 'abono'
      monto: number
      notas: string
      usuarioId: string
    }) => {
      if (tipo === 'abono') {
        return api.post(`/api/clientes/${cliente.id}/abono`, { monto, notas: notas.trim() || null })
      }
      // cargo manual (no debería suceder normalmente — los cargos vienen de ventas)
      return api.post(`/api/clientes/${cliente.id}/abono`, { monto: -monto, notas: notas.trim() || null })
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['clientes'] })
      qc.invalidateQueries({ queryKey: ['cc-movimientos', vars.cliente.id] })
    },
  })
}
